import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, TrendingUpIcon, BarChart3Icon, CheckCircleIcon, ArrowRightIcon, StarIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AdCampaignsPage() {
  const faqs = [{
    question: 'Why should I advertise on Yelp instead of Google?',
    answer: 'Yelp users have exceptionally high purchase intent. When someone searches for a plumber or roofer on Yelp, they are usually looking to hire someone that same day. While Google is great for broad searches, Yelp is the bottom of the funnel for local services.'
  }, {
    question: 'What does it mean to be a Yelp Advertising Premier Partner?',
    answer: "As a Yelp Advertising Premier Partner, we have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies and platform updates that regular advertisers don't get. Premier is Yelp's top partner tier, reserved for agencies with proven performance."
  }, {
    question: 'Do I need a large budget to start?',
    answer: 'No. We work with businesses of all sizes to determine a starting budget that makes sense for your specific industry and local market competition. We focus on maximizing ROI, not just spending money.'
  }, {
    question: 'How do you track if the ads are actually working?',
    answer: "We implement full-funnel tracking. We don't just track clicks or impressions; we track actual phone calls, messages, and form fills generated from the ads, tying them directly back to your revenue."
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-411">
        <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={() => setIsOpen(!isOpen)} data-id="element-412">
          <span className="text-lg font-bold text-white group-hover:text-red-500 transition-colors" data-id="element-413">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-red-500' : ''}`} data-id="element-414" />
        </button>
        <AnimatePresence data-id="element-415">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-416">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-417">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-418">
      <SEOHead title="Yelp Advertising Premier Partner | AI Ad Management | Viridia Analytics Phoenix" description="Expert Yelp Ads and AI ad management for Phoenix local businesses. As a Yelp Advertising Premier Partner, our marketing agency maximizes your ad spend." canonical="/ad-campaigns" keywords="Yelp Ads management Phoenix, Yelp Ads partner agency, AI advertising, AI ad management, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers, local business advertising, ad campaign optimization" faq={faqs} data-id="element-419" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-420">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-421">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-red-500/20 blur-[120px] animate-pulse-slow" data-id="element-422"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-423">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-424">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-425">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-426">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-semibold tracking-wide mb-6" data-id="element-427">
                  PAID ADVERTISING
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-428">
                  Your Yelp Ads{' '}
                  <span className="text-red-500" data-id="element-429">Partner Agency</span>
                </h1>
                <p className="text-lg text-gray-400 mb-4 leading-relaxed" data-id="element-430">
                  Leverage our insider expertise from former Yelp employees to
                  drive qualified leads. We understand CPC optimization,
                  competitive bidding, and seasonality trends that maximize ROI
                  on Yelp for local service businesses.
                </p>
                <p className="text-lg text-red-500 font-medium mb-8 leading-relaxed" data-id="element-431">
                  In the last 30 days, our clients generated 380+ Yelp leads and
                  booked 57 AI estimates across two campaigns.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-432">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-433">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-434">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration */}
            <div className="relative h-[400px] lg:h-[500px] hidden md:block" data-id="element-435">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="absolute inset-0 flex items-center justify-center" data-id="element-436">
                {/* Central Yelp Partner Badge */}
                <motion.div className="relative z-30 flex flex-col items-center" animate={{
                y: [0, -15, 0]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-437">
                  <img src="https://cdn.magicpatterns.com/uploads/rbq8W356ty5oybkr7qHyKz/yelp-premiere-partner-1.png" alt="Yelp Advertising Premier Partner" className="w-80 h-80 object-contain drop-shadow-[0_0_40px_rgba(239,68,68,0.4)]" data-id="element-438" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS SECTION */}
      <section className="py-24 bg-viridia-light relative" data-id="element-439">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-440">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-441">
            <div data-id="element-442">
              <ScrollReveal data-id="element-443">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 text-red-500 text-sm font-semibold tracking-wide uppercase mb-6 border border-red-500/20" data-id="element-444">
                  PLATFORM EXPERTISE
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-445">
                  Why Yelp Ads?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-446">
                  Yelp is where high-intent customers go when they are ready to
                  hire a local service professional. We don't just set and
                  forget. We actively manage your campaigns using insider
                  knowledge to maximize your return on ad spend.
                </p>
                <div className="space-y-6" data-id="element-447">
                  {['TFP Landscaping: 242 leads, 36.4k impressions in 30 days', 'Finish Coat Painting: 138 leads, 33.2k impressions in 30 days', 'Optimize Cost-Per-Click (CPC) to stretch your budget', 'Leverage our Partner Agency priority support'].map((feature, index) => <div key={index} className="flex items-start" data-id="element-448">
                      <CheckCircleIcon className="w-6 h-6 text-red-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-449" />
                      <span className="text-gray-700 font-medium text-lg" data-id="element-450">
                        {feature}
                      </span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-451">
              <ScrollReveal direction="left" data-id="element-452">
                <div className="bg-white rounded-2xl p-10 shadow-xl border-t-4 border-red-500 relative overflow-hidden" data-id="element-453">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full -z-10" data-id="element-454"></div>
                  <div className="w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 mb-8" data-id="element-455">
                    <StarIcon className="w-8 h-8 fill-current" data-id="element-456" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-457">
                    Targeted Local Advertising
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg mb-8" data-id="element-458">
                    Reach customers already looking for businesses like yours on
                    Yelp. Our team includes former Yelp insiders who know
                    exactly how to maximize your ad spend on the platform.
                  </p>
                  <Link to="/contact" className="inline-flex items-center text-red-500 font-bold hover:text-red-600 transition-colors text-lg" data-id="element-459">
                    Start Dominating Yelp{' '}
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-460" />
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* The Real Problem with Ads */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-461">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-462">
          <ScrollReveal data-id="element-463">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-464">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-465">
                Ad Spend is Wasted Without Follow-Up
              </h2>
              <p className="text-xl text-gray-400" data-id="element-466">
                Generating clicks is easy. Turning those clicks into booked jobs
                is hard.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-467">
            <ScrollReveal delay={0.1} data-id="element-468">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-469">
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-470">
                  The Problem
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-471">
                  Most agencies focus solely on getting you more traffic. But if
                  your team is busy on a jobsite and takes hours to respond to a
                  new lead, that lead has already called your competitor. You
                  paid for the click, but lost the job.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-472">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-473">
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-474">
                  The Solution
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-475">
                  We pair our ad management with our AI Lead Conversion system.
                  Every lead generated by your ads receives an instant response,
                  ensuring your ad budget actually turns into booked
                  appointments.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-476">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-477">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-478">
            <ScrollReveal data-id="element-479">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-480">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-481">
                Our ad management services are designed for home service
                businesses that want to dominate their local market on Yelp.
              </p>
              <ul className="space-y-4" data-id="element-482">
                <li className="flex items-start gap-3" data-id="element-483">
                  <BuildingIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-484" />
                  <div data-id="element-485">
                    <h4 className="font-bold text-white" data-id="element-486">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-487">
                      HVAC, painters, landscapers, window installers, plumbers,
                      and roofers looking for high-intent local leads.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-488">
                  <ZapIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-489" />
                  <div data-id="element-490">
                    <h4 className="font-bold text-white" data-id="element-491">
                      Businesses Ready to Scale
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-492">
                      If you have the capacity to take on more jobs and want a
                      predictable lead generation engine.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-493">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-494">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-495">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-496">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-497">
                    <div className="font-bold text-white mb-1" data-id="element-498">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-499">
                      Ensure every ad click gets an instant response.
                    </div>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-500">
                    <div className="font-bold text-white mb-1" data-id="element-501">
                      Conversion Websites
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-502">
                      Send your ad traffic to a site built to convert.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-503">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-504">
          <ScrollReveal data-id="element-505">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-506">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-507">
          <ScrollReveal delay={0.2} data-id="element-508">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-509">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-510" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-511">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-512">
          <ScrollReveal data-id="element-513">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-514">
              Stop wasting ad spend
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-515">
              Book a strategy call to see how we can optimize your ad campaigns
              and ensure every lead is followed up with instantly.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300" data-id="element-516">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-517" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}